import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import {
  countdownTimerContainerStyles,
  countdownTimerProgressContainerStyles,
  countdownTimerProgressStyles,
  countdownTimerBackgroundStyles,
  countdownTimerTextContainerStyles,
  countdownTimerNumberStyles,
  countdownTimerLabelStyles,
} from "./styles";

const numberFontSizeFor = (size: number): string => {
  if (size <= 36) return "0.75rem";
  if (size <= 44) return "0.875rem";
  if (size <= 56) return "1rem";
  return "1.5rem";
};

const labelFontSizeFor = (size: number): string => {
  if (size <= 40) return "0.5rem";
  if (size <= 56) return "0.625rem";
  return "0.75rem";
};

interface CountdownTimerProps {
  totalSeconds: number;
  remainingSeconds: number;
  onTimeUp?: () => void;
  size?: number;
  color?: string;
  textColor?: string;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  totalSeconds,
  remainingSeconds,
  onTimeUp,
  size = 80,
  color = "#1976d2",
  textColor,
}) => {
  const theme = useTheme();
  const [currentSeconds, setCurrentSeconds] = useState(remainingSeconds);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasFiredOnTimeUpRef = useRef(false);

  const defaultTextColor =
    textColor || (theme.palette.mode === "dark" ? "white" : "black");

  useEffect(() => {
    setCurrentSeconds(remainingSeconds);
    if (remainingSeconds > 0) {
      hasFiredOnTimeUpRef.current = false;
    }
  }, [remainingSeconds]);

  useEffect(() => {
    if (currentSeconds <= 0) {
      if (onTimeUp && !hasFiredOnTimeUpRef.current) {
        hasFiredOnTimeUpRef.current = true;
        onTimeUp();
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentSeconds((prev) => {
        const newSeconds = prev - 1;
        if (newSeconds <= 0) {
          if (onTimeUp && !hasFiredOnTimeUpRef.current) {
            hasFiredOnTimeUpRef.current = true;
            onTimeUp();
          }
          return 0;
        }
        return newSeconds;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentSeconds, onTimeUp]);

  const progress = totalSeconds > 0 ? (currentSeconds / totalSeconds) * 100 : 0;

  return (
    <Box sx={countdownTimerContainerStyles}>
      <Box sx={countdownTimerProgressContainerStyles}>
        <svg width={0} height={0}>
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#28C9DC" />
              <stop offset="100%" stopColor="#7B49C1" />
            </linearGradient>
          </defs>
        </svg>
        <CircularProgress
          variant="determinate"
          value={progress}
          size={size}
          thickness={4}
          sx={countdownTimerProgressStyles(color)}
        />
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4}
          sx={countdownTimerBackgroundStyles}
        />
        <Box sx={countdownTimerTextContainerStyles}>
          <Typography
            variant="h5"
            component="div"
            sx={{
              ...countdownTimerNumberStyles(defaultTextColor),
              fontSize: numberFontSizeFor(size),
            }}
          >
            {currentSeconds}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{
          ...countdownTimerLabelStyles(defaultTextColor),
          fontSize: labelFontSizeFor(size),
          mt: size <= 44 ? 0.25 : 0.5,
        }}
      >
        Time Left
      </Typography>
    </Box>
  );
};
